// Klaviyo can close the embed right after a successful submit and empty the mount shortly after.
const EMPTY_CHECK_DELAY_MS = 150;
// Give up on the loading skeleton when the embed never renders (form not published, script blocked).
const FORM_TIMEOUT_MS = 45000;

class MastonCommercialForm extends HTMLElement {
  #observer;
  #events;
  #checkTimer;
  #loadTimer;
  #submitted = false;

  connectedCallback() {
    this.#observer?.disconnect();
    this.#events?.abort();
    this.#submitted = false;
    const mount = this.querySelector('[data-form-mount]');
    const template = this.querySelector('template[data-form-icon]');
    if (!mount || !template) return;

    const syncIcon = () => {
      // Match the submit row used by the embed's CSS, including trailing helper rows.
      const button = mount.querySelector(
        '[data-testid="form-row"]:nth-last-child(1 of :has(button.klaviyo-form-button)) button.klaviyo-form-button'
      );
      if (!button || button.querySelector('[data-form-submit-icon]')) return;
      const icon = template.content.cloneNode(true);
      const svg = icon.querySelector('svg');
      if (!svg) return;
      svg.setAttribute('data-form-submit-icon', '');
      svg.setAttribute('aria-hidden', 'true');
      // The shared glyph contains an unreferenced ID; avoid duplicate IDs across embeds.
      svg.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      button.append(icon);
    };

    this.#observer = new MutationObserver(syncIcon);
    this.#observer.observe(mount, { childList: true, subtree: true });
    syncIcon();
    this.#watchSubmission(mount);
    clearTimeout(this.#loadTimer);
    this.#loadTimer = setTimeout(() => {
      if (mount.childElementCount === 0) mount.setAttribute('data-form-unavailable', '');
    }, FORM_TIMEOUT_MS);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#events?.abort();
    clearTimeout(this.#checkTimer);
    clearTimeout(this.#loadTimer);
  }

  #watchSubmission(mount) {
    const formId = [...mount.classList]
      .map((name) => /^klaviyo-form-(\w+)$/.exec(name)?.[1])
      .find(Boolean);
    if (!formId) return;

    this.#events = new AbortController();
    window.addEventListener('klaviyoForms', (event) => {
      const detail = event.detail;
      if (detail?.formId !== formId) return;
      // Only a real submission may be reported as success: a button configured to just close the form also fires "close".
      if (detail.type === 'submit') {
        this.#submitted = true;
        if (mount.hasAttribute('data-form-closed')) this.#showSuccess(mount);
        return;
      }
      if (detail.type !== 'close') return;
      // Stops the loading skeleton, which would otherwise match the now empty mount.
      mount.setAttribute('data-form-closed', '');
      clearTimeout(this.#checkTimer);
      this.#checkTimer = setTimeout(() => this.#showSuccess(mount), EMPTY_CHECK_DELAY_MS);
    }, { signal: this.#events.signal });
  }

  #showSuccess(mount) {
    // If the embed kept its own success message on screen, that one wins.
    if (!this.#submitted || mount.childElementCount > 0) return;
    const note = this.querySelector('[data-success-message]');
    if (note) note.textContent = note.dataset.successMessage;
  }
}

if (!customElements.get('maston-commercial-form')) {
  customElements.define('maston-commercial-form', MastonCommercialForm);
}
